import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  ClipboardPlus,
  ExternalLink,
  FileHeart,
  FileSearch,
  HeartPulse,
  Pencil,
  Plus,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { supabase } from '../../config/supabase';
import {
  PATIENT_CLINICAL_EVENT_LABELS,
  PatientClinicalTimelineService,
  type PatientTimelineItem,
} from '../../services/PatientClinicalTimelineService';
import { CLINICAL_HISTORY_TYPE_LABELS } from '../../utils/clinicalDocuments';
import { isInitialAnamnesisRecord, isStructuredAnamnesisRecord } from '../../utils/clinicalDocuments';
import { buildClinicalAssessmentPath } from '../../utils/clinicalAssessmentNavigation';
import { getMedicalRecordDownloadUrl } from '../../utils/medicalRecordFiles';
import ClinicalEventModal, { type EditableClinicalEvent } from './ClinicalEventModal';

interface PatientClinicalTimelineProps {
  patientId: string;
  clientId: string;
  patientName?: string;
  userId: string;
  userDisplayName?: string | null;
}

const ITEM_LABELS: Record<PatientTimelineItem['kind'], string> = {
  initial_anamnesis: 'HMA inicial',
  clinical_history: 'Histórico clínico',
  evaluation: 'Avaliação',
  exam: 'Exame',
  ...PATIENT_CLINICAL_EVENT_LABELS,
};

const plainText = (value: string) => {
  if (typeof document === 'undefined') return value;
  const container = document.createElement('div');
  container.innerHTML = value;
  return container.textContent || container.innerText || value;
};

const getItemLabel = (item: PatientTimelineItem) =>
  item.clinicalHistoryType
    ? CLINICAL_HISTORY_TYPE_LABELS[item.clinicalHistoryType]
    : ITEM_LABELS[item.kind];

const PatientClinicalTimeline: React.FC<PatientClinicalTimelineProps> = ({
  patientId,
  clientId,
  patientName,
  userId,
  userDisplayName,
}) => {
  const location = useLocation();
  const [items, setItems] = useState<PatientTimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<EditableClinicalEvent | null>(null);

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await PatientClinicalTimelineService.getTimeline(patientId, clientId));
    } catch (loadError: unknown) {
      console.error('Erro ao carregar timeline clínica:', loadError);
      const errorCode =
        typeof loadError === 'object' && loadError !== null && 'code' in loadError
          ? String(loadError.code)
          : '';
      setError(
        errorCode === '42P01'
          ? 'A timeline ainda não está habilitada no banco de dados desta clínica.'
          : 'Não foi possível carregar a timeline clínica.',
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, patientId]);

  useEffect(() => {
    void loadTimeline();
  }, [loadTimeline]);

  useEffect(() => {
    const channel = supabase
      .channel(`patient-clinical-timeline-${clientId}-${patientId}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'patient_clinical_events', filter: `patientId=eq.${patientId}` },
        () => void loadTimeline(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'medical_records', filter: `patientId=eq.${patientId}` },
        () => void loadTimeline(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'assessment_results_v2', filter: `patientId=eq.${patientId}` },
        () => void loadTimeline(),
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [clientId, loadTimeline, patientId]);

  const hasClinicalHistory = useMemo(
    () => items.some((item) => item.kind === 'initial_anamnesis' || item.kind === 'clinical_history'),
    [items],
  );

  const getItemStyle = (item: PatientTimelineItem) => {
    if (item.kind === 'initial_anamnesis' || item.kind === 'clinical_history') {
      return { icon: FileHeart, iconClass: 'bg-teal-100 text-teal-700', borderClass: 'border-teal-200' };
    }
    if (item.kind === 'evaluation' || item.kind === 'exam') {
      return { icon: FileSearch, iconClass: 'bg-sky-100 text-sky-700', borderClass: 'border-sky-200' };
    }
    if (item.kind === 'improvement') {
      return { icon: HeartPulse, iconClass: 'bg-emerald-100 text-emerald-700', borderClass: 'border-emerald-200' };
    }
    if (item.kind === 'hospital_discharge' || item.kind === 'treatment_discharge') {
      return { icon: HeartPulse, iconClass: 'bg-teal-100 text-teal-700', borderClass: 'border-teal-200' };
    }
    if (item.severity === 'high') {
      return { icon: AlertCircle, iconClass: 'bg-red-100 text-red-700', borderClass: 'border-red-200' };
    }
    return { icon: Activity, iconClass: 'bg-amber-100 text-amber-700', borderClass: 'border-amber-200' };
  };

  const openExam = async (item: PatientTimelineItem) => {
    try {
      const downloadUrl = await getMedicalRecordDownloadUrl(item.recordAttachments);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (openError) {
      console.error('Erro ao abrir exame da timeline:', openError);
      toast.error('Não foi possível abrir o arquivo deste exame.');
    }
  };

  const openNewEvent = () => {
    setEventToEdit(null);
    setShowEventModal(true);
  };

  const openEventEditor = (item: PatientTimelineItem) => {
    if (item.source !== 'clinical_event') return;

    setEventToEdit({
      eventId: item.id,
      eventType: item.kind as EditableClinicalEvent['eventType'],
      severity: item.severity || 'moderate',
      eventTitle: item.title,
      eventDescription: item.description,
      eventDate: item.date,
    });
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setEventToEdit(null);
  };

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-950 p-5 text-white shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-200">Linha do cuidado</p>
            <h2 className="mt-1 text-xl font-semibold">Timeline de {patientName || 'paciente'}</h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-300">
              Marcos clínicos relevantes para entender mudanças no quadro, sem misturá-los às evoluções de sessão.
            </p>
          </div>
          <button
            type="button"
            onClick={openNewEvent}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-teal-400 px-4 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-teal-300"
          >
            <Plus className="h-4 w-4" />
            Novo registro
          </button>
        </div>
      </div>

      {!hasClinicalHistory && !loading && !error && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <ClipboardPlus className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Paciente sem histórico clínico registrado</p>
            <p className="mt-1 text-sm text-amber-800">
              Um registro pode ser criado ao abrir “Nova evolução” e ficará disponível nesta timeline.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Carregando timeline clínica...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Activity className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700">Nenhum marco clínico registrado</p>
          <p className="mt-1 text-sm text-slate-500">A timeline começa com um registro do histórico ou um evento relevante.</p>
        </div>
      ) : (
        <div className="relative space-y-4 before:absolute before:bottom-5 before:left-5 before:top-5 before:w-px before:bg-slate-200">
          {items.map((item) => {
            const style = getItemStyle(item);
            const Icon = style.icon;
            return (
              <article key={`${item.source}-${item.id}`} className="relative flex gap-4">
                <div className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.iconClass}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className={`min-w-0 flex-1 rounded-xl border bg-white p-4 shadow-sm ${style.borderClass}`}>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {getItemLabel(item)}
                      </span>
                      <h3 className="mt-1 font-semibold text-slate-900">{item.title}</h3>
                    </div>
                    <time className="shrink-0 text-xs text-slate-500">
                      {new Date(item.date).toLocaleString('pt-BR')}
                    </time>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                    {plainText(item.description)}
                  </p>
                  {item.source === 'medical_record' && (
                    isInitialAnamnesisRecord(item.recordAttachments)
                    || isStructuredAnamnesisRecord(item.recordAttachments)
                    || ['anamnesis', 'initial_anamnesis', 'anamnesis_update'].includes(item.clinicalHistoryType || '')
                  ) && (
                    <Link
                      to={buildClinicalAssessmentPath(location.pathname, patientId, item.id)}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100"
                    >
                      <ClipboardPlus className="h-4 w-4" />
                      Abrir avaliação com esta anamnese
                    </Link>
                  )}
                  {item.kind === 'exam' && Boolean(item.recordAttachments) && (
                    <button
                      type="button"
                      onClick={() => void openExam(item)}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-100"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Abrir exame
                    </button>
                  )}
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                    {item.authorName ? (
                      <p className="text-xs text-slate-500">Registrado por {item.authorName}</p>
                    ) : <span />}
                    {item.source === 'clinical_event' && (
                      <button
                        type="button"
                        onClick={() => openEventEditor(item)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:border-teal-300 hover:bg-teal-50 hover:text-teal-800"
                        aria-label={`Editar ${item.title}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <ClinicalEventModal
        isOpen={showEventModal}
        patientId={patientId}
        clientId={clientId}
        patientName={patientName}
        userId={userId}
        userDisplayName={userDisplayName}
        eventToEdit={eventToEdit}
        onClose={closeEventModal}
        onSaved={loadTimeline}
      />
    </div>
  );
};

export default PatientClinicalTimeline;
